// §4 S14 — Piece-rate entries, /production/piece-rate (Module 04).
//
// THE DEFECT THIS CLOSES is the mirror image of a dead control. §5 e27 has
// served the §2.13 capture ledger since 04-C-4 — contract-registered, fenced,
// walkthrough-probed — and had ZERO consumers in the product: a hook in
// `production-queries.ts` with no call site, and §4 promising no screen. Every
// row it serves is a QUANTITY THAT WILL BE PAID FOR. A payable quantity nobody
// can list is a payable quantity nobody can argue about, on the one module
// whose flagship claim (§3.7) is that its numbers ARE arguable. D-139 met this
// exact class two days earlier on e22 and answered it the same way: the surface
// is built, because the capability is real (see the §4 row 14 argument).
//
// WHAT THIS SCREEN IS, precisely: the capture ledger, not a payslip. It answers
// "which worker earned piece quantity, on which shift, on which step, and has
// anything been paid for it yet". It cannot answer "how much are they owed",
// and it says so IN WORDS rather than leaving a blank column to be guessed at:
// §12 Q1 (who owns the rate table) is open, nothing in this release prices a
// drum, and a rate this module invented is a number a worker would find on a
// payslip. S11 makes the identical statement to the worker's own phone.
//
// THE THREE HONEST ABSENCES, each stated on the glass rather than dashed:
//
//  * MONEY, BY PERMISSION. `rate_minor`/`amount_minor` are ABSENT — no key at
//    all — for a principal outside §2.13's visibility list (§7's redaction
//    shape, D-074 pt 3). The COLUMNS then do not exist (D-118: a control absent
//    for a permission reason is ABSENT, never disabled or dashed), and the
//    reader is told the ledger is showing quantity only. An absent key is never
//    rendered as 0 and never as "—", because "—" is what an UNPRICED row says.
//  * MONEY, BY MATURITY. When the keys ARE present they are null in this
//    release. That is the other half of D-118 — dead for a FIXABLE reason, so
//    the columns STAY and name the reason in words.
//  * NO ROWS, BECAUSE NOTHING IS ELIGIBLE. An entry exists only for a floor qty
//    report on an operation whose §2.2 `piece_rate_eligible` flag is set, and
//    that flag is authored on the ROUTING STEP (spec 01 §2.11, D-087 ruling 6).
//    No office screen writes it today — the routing editor's step drawer offers
//    `requires_measured_capture` and not this one — so the empty state names
//    that as the reason instead of implying the floor has reported nothing.
//
// WHAT IT WILL NOT INVENT. e27 carries `wo_operation_id` and no work-order
// number, so the step is an IDENTIFIER (a short code chip), never a fabricated
// name and never a link to a route that cannot take it. It carries no unit of
// measure either — the same gap S5 and S12 record against e17 — so quantities
// are bare numbers, because a metre labelled as a kilogram on a pay-relevant
// figure is worse than an unlabelled one. Both are CONTRACT fixes (e27 serving
// `wo_no`/`seq_no`/`uom`), named as successor work in §4 row 14 and deliberately
// not taken here: a contract change must land with its regenerated api-client in
// the same commit, which D-139 already ruled puts it outside a screen change.
//
// Roles: finance / facility_admin / group_admin, and floor_worker on its own
// rows (`production.piece_rate.read` — e27's own fence, no new code). The
// own-rows narrowing is SERVICE-side (D-040 two-layer, keyed on grants) and is
// therefore never claimed by this screen as if it had applied it.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { FacilityFilter } from "../components/facility-filter";
import { RefLabel } from "../components/ref-label";
import { useProductionPermissions } from "../hooks/permissions";
import {
  useFacilities,
  usePieceRateEntries,
  useWorkerIndex,
  type PieceRateEntry,
  type PieceRateQuery,
  type PieceRateStatus,
} from "../hooks/production-queries";
import { formatBusinessDate, formatMinor, formatQty, formatTimestamp } from "../lib/format";
import { validatePieceRateSearch } from "./search-params";
import { shortId } from "../../../../lib/short-id";

/** One page of e27. The endpoint's own default is 50; this is the LOAD-MORE
 * grain, so the number is stated once and the "there is more" branch reads it
 * rather than a literal typed twice. */
export const PIECE_RATE_PAGE = 50;

/**
 * Are the money keys on the page at all?
 *
 * EVERY row, not SOME. The serializer decides per ROW (own row OR
 * `production.view_costs` — §2.13), so a mixed page is expressible even though
 * the seeded grants make it unreachable: a principal without the cost grant is
 * pinned to its own rows, where money is visible. Rendering the columns for a
 * partial page would put "not visible to you" cells beside "no rate yet" cells
 * in one column — the exact ambiguity §7's key-absence shape exists to prevent.
 * So the columns are all-or-nothing, and the partial case says why they are
 * gone instead of dropping them silently.
 */
export function pieceMoneyVisibility(rows: readonly PieceRateEntry[]): {
  show: boolean;
  partial: boolean;
} {
  if (rows.length === 0) return { show: false, partial: false };
  const withMoney = rows.filter((row) => row.amount_minor !== undefined).length;
  if (withMoney === rows.length) return { show: true, partial: false };
  return { show: false, partial: withMoney > 0 };
}

/** §2.13's three values, each with its own literal key — never a computed
 * `t()` prefix, so the i18n reference gate can see all three. The switch is
 * exhaustive over the wire union; an unknown fourth degrades to its raw token,
 * visible rather than blank (the unknown-kind rule). */
export function pieceStatusLabel(t: TFunction, status: PieceRateStatus): string {
  switch (status) {
    case "captured":
      return t("production.pieceRate.statusCaptured");
    case "priced":
      return t("production.pieceRate.statusPriced");
    case "settled":
      return t("production.pieceRate.statusSettled");
    default:
      return status;
  }
}

/**
 * The two MONEY columns, built here so the render path reads as one list and the
 * absent case is a missing pair rather than two conditionals inside cells.
 *
 * A null renders as the em dash and NEVER as 0: `null` means "no rate has been
 * applied to this quantity yet", which is a true statement about §12 Q1 and not
 * a claim that the worker earned nothing.
 */
function moneyColumns(t: TFunction, locale: string): DataTableColumn<PieceRateEntry>[] {
  return [
    {
      key: "rate_minor",
      header: t("production.pieceRate.colRate"),
      numeric: true,
      render: (row) => (
        <span data-testid={`piece-rate-rate-${row.id}`}>
          {row.rate_minor == null
            ? t("production.common.notAvailable")
            : formatMinor(row.rate_minor, row.currency, locale)}
        </span>
      ),
    },
    {
      key: "amount_minor",
      header: t("production.pieceRate.colAmount"),
      numeric: true,
      render: (row) => (
        <span data-testid={`piece-rate-amount-${row.id}`}>
          {row.amount_minor == null
            ? t("production.common.notAvailable")
            : formatMinor(row.amount_minor, row.currency, locale)}
        </span>
      ),
    },
  ];
}

/** Neutral until money is involved: `captured` is a quantity nobody has priced,
 * `priced` is money proposed (accent — the current thing), `settled` is money
 * paid and immutable (§9.17). */
function pieceStatusVariant(status: PieceRateStatus): "neutral" | "accent" | "ok" {
  switch (status) {
    case "priced":
      return "accent";
    case "settled":
      return "ok";
    default:
      return "neutral";
  }
}

export function PieceRateScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const permissions = useProductionPermissions();

  // FAIL-CLOSED, AND THE READ IS NOT ISSUED AT ALL (the S13/UX-4 rule): a role
  // without the code would otherwise watch a skeleton turn into a 403 card
  // offering a Retry that can never succeed.
  //
  // A FAILED role feed is "we cannot tell", NOT "you are not permitted" (the
  // rule stated on `useProductionPermissions` itself, and the reason a custom
  // tenant role — which resolves to zero grants by design — must not meet a
  // refusal this client invented). When the mirror errored the request GOES,
  // and the reader gets the SERVER's own answer with a request id.
  const mayRead = permissions.has("production.piece_rate.read") || permissions.isFeedError;
  const refused = permissions.isResolved && !mayRead;

  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validatePieceRateSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );

  const facilities = useFacilities(mayRead);
  const workers = useWorkerIndex(mayRead && permissions.canReadMembers);

  const baseQuery = useMemo<PieceRateQuery>(() => {
    const query: PieceRateQuery = { limit: PIECE_RATE_PAGE };
    if (search.facility_id !== undefined) query.facility_id = search.facility_id;
    if (search.shift_date_from !== undefined) query.shift_date_from = search.shift_date_from;
    if (search.shift_date_to !== undefined) query.shift_date_to = search.shift_date_to;
    if (search.status !== undefined) query.status = search.status;
    return query;
  }, [search]);

  // Cursor pagination as LOAD MORE — the S1 pattern: earlier pages are kept in
  // state, the live query serves the page at the current cursor, and a filter
  // change resets so stale rows cannot flash under a new period.
  const filterKey = JSON.stringify(baseQuery);
  const [paged, setPaged] = useState<{
    key: string;
    cursor: string | undefined;
    rows: readonly PieceRateEntry[];
  }>({ key: filterKey, cursor: undefined, rows: [] });
  if (paged.key !== filterKey) {
    setPaged({ key: filterKey, cursor: undefined, rows: [] });
  }

  const query = useMemo<PieceRateQuery>(
    () => (paged.cursor !== undefined ? { ...baseQuery, cursor: paged.cursor } : baseQuery),
    [baseQuery, paged.cursor],
  );
  const list = usePieceRateEntries(query, mayRead);

  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);

  const rows = useMemo<readonly PieceRateEntry[]>(
    () => [...paged.rows, ...(list.data?.items ?? [])],
    [paged.rows, list.data],
  );

  const money = pieceMoneyVisibility(rows);
  // Every visible amount is null: §12 Q1's rate table does not exist. Said as a
  // sentence under the table, because a column of dashes on a pay-adjacent
  // screen reads as a system that lost the numbers.
  const nothingPriced = money.show && rows.every((row) => row.amount_minor == null);

  const facilityById = useMemo(() => {
    const map = new Map<string, { name: string; timezone: string | null }>();
    for (const facility of facilities.data?.items ?? []) {
      map.set(facility.id, { name: facility.name, timezone: facility.timezone });
    }
    return map;
  }, [facilities.data]);

  const filtered =
    search.facility_id !== undefined ||
    search.shift_date_from !== undefined ||
    search.shift_date_to !== undefined ||
    search.status !== undefined;

  /** One writer for the URL state, re-validated through the route's OWN
   * validator: an empty picker drops its param rather than pinning `?status=`
   * to nothing, and a param this screen does not understand cannot be
   * reintroduced by a merge. */
  const setSearch = (next: Record<string, string | undefined>): void => {
    const merged = validatePieceRateSearch({ ...search, ...next });
    void navigate({ to: "/production/piece-rate", search: merged, replace: true });
  };

  const columns: DataTableColumn<PieceRateEntry>[] = [
    {
      key: "shift_date",
      // §9.7: the date the SHIFT STARTED, which is the date payroll counts a
      // night shift under. The note beside the pickers says so once.
      header: t("production.pieceRate.colShiftDate"),
      id: true,
      render: (row) => (
        <span data-testid={`piece-rate-date-${row.id}`}>
          {formatBusinessDate(row.shift_date, i18n.language)}
        </span>
      ),
    },
    {
      key: "worker_id",
      header: t("production.pieceRate.colWorker"),
      render: (row) => (
        <span data-testid={`piece-rate-worker-${row.id}`}>
          <RefLabel
            value={workers.byId.get(row.worker_id)}
            id={row.worker_id}
            pending={workers.isPending}
          />
        </span>
      ),
    },
    {
      key: "facility_id",
      header: t("production.pieceRate.colFacility"),
      render: (row) => (
        <RefLabel
          value={facilityById.get(row.facility_id)?.name}
          id={row.facility_id}
          pending={facilities.isPending}
        />
      ),
    },
    {
      key: "wo_operation_id",
      header: t("production.pieceRate.colOperation"),
      // e27 carries the operation's ID and nothing that names it — no work
      // order number, no step name, no sequence. A short code chip reads as an
      // identifier (support can quote it); a uuid in a name column reads as
      // corrupt data, and an invented name would be worse than either.
      render: (row) => <code className="ref-id">{shortId(row.wo_operation_id)}</code>,
    },
    {
      key: "qty",
      header: t("production.pieceRate.colQty"),
      numeric: true,
      // NO UNIT beside it, and that is a stated gap rather than an omission:
      // e27 serves none (the S5/S12 note about e17, same shape), and a metre
      // labelled as a kilogram on a figure somebody will be paid for is worse
      // than an unlabelled one.
      render: (row) => (
        <span data-testid={`piece-rate-qty-${row.id}`}>
          {formatQty(row.qty, { locale: i18n.language })}
        </span>
      ),
    },
    ...(money.show ? moneyColumns(t, i18n.language) : []),
    {
      key: "status",
      header: t("production.pieceRate.colStatus"),
      render: (row) => (
        <StatusChip
          variant={pieceStatusVariant(row.status)}
          data-testid={`piece-rate-status-${row.id}`}
        >
          {pieceStatusLabel(t, row.status)}
        </StatusChip>
      ),
    },
    {
      key: "reported_at",
      header: t("production.pieceRate.colReported"),
      age: true,
      // The instant is rendered in the FACILITY's zone (D-089): a report made at
      // 23:40 on the plant's clock is what the shift it is filed under means,
      // and the office reader may be sitting three time zones west of it.
      render: (row) =>
        formatTimestamp(
          row.reported_at,
          i18n.language,
          facilityById.get(row.facility_id)?.timezone ?? null,
        ),
    },
  ];

  const paused = list.fetchStatus === "paused";
  const loading = !refused && list.isPending && !paused;
  const failed = list.error !== null && !(list.error instanceof UnauthorizedError);

  return (
    <section className="piece-rate-screen" data-testid="piece-rate-screen">
      <PageHeader title={t("production.pieceRate.title")} />

      {refused ? (
        <ForbiddenState
          title={t("production.pieceRate.forbiddenTitle")}
          body={t("production.pieceRate.forbiddenBody")}
        />
      ) : (
        <>
          <div className="piece-rate-filters" data-testid="piece-rate-filters">
            {/* ALL FACILITIES is allowed and is the default: a payroll period is
                asked about a GROUP as often as about one plant, and e27's
                `facility_id` is optional. */}
            <FacilityFilter
              value={search.facility_id ?? ""}
              allowAll
              facilities={facilities.data?.items ?? []}
              testId="piece-rate-facility"
              onChange={(value) => {
                setSearch({ facility_id: value.length > 0 ? value : undefined });
              }}
            />
            <Field label={t("production.pieceRate.dateFrom")}>
              <input
                type="date"
                data-testid="piece-rate-from"
                value={search.shift_date_from ?? ""}
                onChange={(event) => {
                  setSearch({ shift_date_from: event.target.value });
                }}
              />
            </Field>
            <Field label={t("production.pieceRate.dateTo")}>
              <input
                type="date"
                data-testid="piece-rate-to"
                value={search.shift_date_to ?? ""}
                onChange={(event) => {
                  setSearch({ shift_date_to: event.target.value });
                }}
              />
            </Field>
            <Field label={t("production.pieceRate.statusFilter")}>
              <select
                data-testid="piece-rate-status"
                value={search.status ?? ""}
                onChange={(event) => {
                  setSearch({ status: event.target.value });
                }}
              >
                <option value="">{t("production.pieceRate.statusAny")}</option>
                <option value="captured">{t("production.pieceRate.statusCaptured")}</option>
                <option value="priced">{t("production.pieceRate.statusPriced")}</option>
                <option value="settled">{t("production.pieceRate.statusSettled")}</option>
              </select>
            </Field>
          </div>
          {/* §9.7, said where the filter is rather than in a tooltip nobody
              opens — the same sentence S5 makes about the same date. */}
          <p className="piece-rate-note" data-testid="piece-rate-date-note">
            {t("production.pieceRate.shiftDateNote")}
          </p>
        </>
      )}

      {!refused && paused ? (
        <OfflineState
          title={t("production.pieceRate.offlineTitle")}
          body={t("production.pieceRate.offlineBody")}
        />
      ) : null}

      {loading ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("production.common.loading")} />
      ) : null}

      {!refused && failed ? (
        <ErrorState
          title={t("production.pieceRate.error")}
          retryLabel={t("production.common.retry")}
          requestIdLabel={t("production.common.requestId")}
          requestId={requestIdOf(list.error)}
          onRetry={() => {
            void list.refetch();
          }}
        />
      ) : null}

      {!refused && !loading && !paused && !failed && rows.length === 0 ? (
        // TWO DIFFERENT EMPTIES, because they are two different facts. A
        // filtered empty is about the filters. An UNFILTERED empty is about the
        // product: no routing step is flagged piece-rate eligible, and D-118's
        // second half says a surface dead for a fixable reason names the reason
        // in words instead of leaving the reader to conclude the floor reported
        // nothing.
        <EmptyState
          title={
            filtered
              ? t("production.pieceRate.emptyFilteredTitle")
              : t("production.pieceRate.emptyTitle")
          }
          body={
            filtered
              ? t("production.pieceRate.emptyFilteredBody")
              : t("production.pieceRate.emptyBody")
          }
        />
      ) : null}

      {!refused && rows.length > 0 ? (
        <>
          <DataTable
            data-testid="piece-rate-table"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            rowTestId="piece-rate-row"
            empty={null}
            caption={t("production.pieceRate.tableCaption")}
          />

          {/* WHY THE MONEY COLUMNS SAY NOTHING YET — the fixable half of D-118.
              Without this line a column of dashes on a pay-adjacent ledger reads
              as a system that lost the numbers. */}
          {nothingPriced ? (
            <p className="piece-rate-note" data-testid="piece-rate-unpriced">
              {t("production.pieceRate.unpricedNote")}
            </p>
          ) : null}

          {/* The money columns are ABSENT for a permission reason. Absence is
              the §7 shape and it is deliberately not dashed — but an absence
              nobody explains is indistinguishable from a screen that forgot the
              column, so the reason is a sentence. */}
          {!money.show ? (
            <p className="piece-rate-note" data-testid="piece-rate-no-money">
              {money.partial
                ? t("production.pieceRate.moneyPartialNote")
                : t("production.pieceRate.moneyHiddenNote")}
            </p>
          ) : null}

          {list.data !== undefined && list.data.next_cursor !== null
            ? (() => {
                // Bound OUTSIDE the handler so the narrowing above is the one
                // the click uses: `list.data` is a query result and may have
                // been replaced by the time the button is pressed.
                const page = list.data;
                const cursor = page.next_cursor ?? undefined;
                return (
                  <Button
                    data-testid="piece-rate-load-more"
                    onClick={() => {
                      setPaged((prev) => ({
                        key: prev.key,
                        cursor,
                        rows: [...prev.rows, ...page.items],
                      }));
                    }}
                  >
                    {t("production.pieceRate.loadMore")}
                  </Button>
                );
              })()
            : null}
        </>
      ) : null}
    </section>
  );
}
